import { useCallback, useRef, useState } from "react";

type Options = {
  /** Should gating be active (e.g. stagedUploads.length > 0)? */
  enabled: boolean;
  /** Called only when proceed === true, after replay completes */
  onProceed?: () => void;
  uploadSelector?: string; // default: [data-upload-root]
  modalSelector?: string;  // default: [aria-modal]
};

export function useClickGate({
  enabled,
  onProceed,
  uploadSelector = "[data-upload-root]",
  modalSelector = "[aria-modal]",
}: Options) {
  const [open, setOpen] = useState(false);

  const isReplayingRef = useRef(false);
  const pendingTargetRef = useRef<HTMLElement | null>(null);
  const pendingPointRef = useRef<{ x: number; y: number } | null>(null);

  const findClickable = (el: HTMLElement | null) =>
    el?.closest<HTMLElement>(
      'button, a[href], input[type="button"], input[type="submit"], button[type="submit"], [role="button"]'
    ) || el || null;

  const isSubmitter = (el: Element | null) =>
    !!el && (el.matches('button[type="submit"]') || el.matches('input[type="submit"]'));

  const requestFormSubmit = (submitter: HTMLButtonElement | HTMLInputElement) => {
    const form = submitter.form || submitter.closest("form");
    if (!form?.isConnected || !submitter.isConnected) return;
    (form.requestSubmit ? form.requestSubmit(submitter) : submitter.click());
  };

  const onRootClickCapture = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || isReplayingRef.current) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest(uploadSelector) || t?.closest(modalSelector)) return;

      e.preventDefault();
      e.stopPropagation();
      pendingTargetRef.current = t ?? null;
      pendingPointRef.current = { x: e.clientX, y: e.clientY };
      setOpen(true);
    },
    [enabled, uploadSelector, modalSelector]
  );

  /** Single close function */
  const close = useCallback(
    (proceed: boolean) => {
      setOpen(false);

      if (!proceed) {
        pendingTargetRef.current = null;
        pendingPointRef.current = null;
        return;
      }

      requestAnimationFrame(() => {
        const pt = pendingPointRef.current;
        let base = pt ? (document.elementFromPoint(pt.x, pt.y) as HTMLElement | null) : null;
        if (!base) base = pendingTargetRef.current;
        if (!base) return;

        const clickable = findClickable(base);
        if (!clickable) return;

        isReplayingRef.current = true;
        try {
          if (isSubmitter(clickable)) {
            requestFormSubmit(clickable as HTMLButtonElement | HTMLInputElement);
          } else if (clickable.isConnected) {
            clickable.dispatchEvent(
              new MouseEvent("click", { bubbles: true, cancelable: true, composed: true, view: window })
            );
          }
        } finally {
          setTimeout(() => (isReplayingRef.current = false), 0);
          pendingTargetRef.current = null;
          pendingPointRef.current = null;
          onProceed?.();
        }
      });
    },
    [onProceed]
  );

  return { open, onRootClickCapture, close };
}